import React, { useState } from 'react';
import { 
  X, 
  TrendingUp, 
  TrendingDown, 
  Eye, 
  Users, 
  Target,
  Lightbulb,
  FileText,
  Play,
  BarChart3,
  DollarSign,
  Clock,
  Star,
  Award,
  Calendar,
  Zap,
  CheckCircle,
  XCircle,
  MessageSquare,
  ThumbsUp
} from 'lucide-react';
import './TopScriptModal.css';

const TopScriptModal = ({ script, onClose, onViewBrief }) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Mock data specific to this script (replace with real data)
  const scriptStats = {
    title: script.title,
    totalViews: script.views,
    totalEngagement: Math.round(script.views * 0.052), // 5.2% engagement rate
    avgCTR: script.ctr,
    avgConversion: script.conversion,
    totalRevenue: Math.round(script.views * script.conversion * 0.01 * 15), // Estimated revenue
    usageCount: script.usageCount,
    avgRating: script.avgRating,
    hookRating: 4.7,
    ctaRating: 4.3,
    avgWatchTime: 48,
    completionRate: 82,
    trend: '+15%',
    createdDate: '2024-01-10',
    brief: "Summer Product Launch",
    topCreators: [
      {
        name: "Sarah Johnson",
        avatar: "https://i.pravatar.cc/64?img=44",
        views: 180000,
        ctr: 7.2,
        conversion: 4.1,
        rating: 4.8
      },
      {
        name: "Mike Chen",
        avatar: "https://i.pravatar.cc/64?img=32",
        views: 145000,
        ctr: 6.8,
        conversion: 3.9,
        rating: 4.6
      },
      {
        name: "Emma Davis",
        avatar: "https://i.pravatar.cc/64?img=65",
        views: 120000,
        ctr: 6.1,
        conversion: 3.5,
        rating: 4.4
      }
    ],
    recentUsage: [
      {
        creator: "Alex Smith",
        date: "2024-01-20",
        views: 85000,
        performance: "excellent"
      },
      {
        creator: "Jordan Lee",
        date: "2024-01-18",
        views: 72000,
        performance: "good"
      },
      {
        creator: "Taylor Wilson",
        date: "2024-01-15",
        views: 68000,
        performance: "good"
      }
    ]
  };

  const scriptInsights = [
    {
      type: "positive",
      title: "High Creator Adoption",
      insight: `This script has been used by ${scriptStats.usageCount} creators with an average rating of ${scriptStats.avgRating}/5`,
      recommendation: "Consider creating similar script variations for different niches"
    },
    {
      type: "improvement",
      title: "Hook vs CTA Performance",
      insight: `Hook rating (${scriptStats.hookRating}) is higher than CTA rating (${scriptStats.ctaRating})`,
      recommendation: "Focus on improving call-to-action clarity and placement"
    },
    {
      type: "warning",
      title: "Performance Variance",
      insight: "Creator performance varies significantly (180K vs 68K views)",
      recommendation: "Provide better creator training on script execution"
    }
  ];

  return (
    <div className="top-script-modal-overlay" onClick={onClose}>
      <div className="top-script-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="top-script-modal-header">
          <div className="top-script-header-content">
            <div className="top-script-title-section">
              <h2>{scriptStats.title}</h2>
              <div className="top-script-badge">Top Performing Script</div>
            </div>
            <button className="top-script-view-btn" onClick={() => onViewBrief(script)}>
              <FileText size={16} />
              View Script
            </button>
          </div>
          <button className="top-script-modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="top-script-modal-tabs">
          <button 
            className={`top-script-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <BarChart3 size={16} />
            Script Overview
          </button>
          <button 
            className={`top-script-tab-btn ${activeTab === 'creators' ? 'active' : ''}`}
            onClick={() => setActiveTab('creators')}
          >
            <Users size={16} />
            Creator Usage
          </button>
          <button 
            className={`top-script-tab-btn ${activeTab === 'insights' ? 'active' : ''}`}
            onClick={() => setActiveTab('insights')}
          >
            <Lightbulb size={16} />
            Script Insights
          </button>
        </div>

        <div className="top-script-modal-content">
          {activeTab === 'overview' && (
            <div className="top-script-overview-tab">
              <div className="top-script-brief-section">
                <h3>Script Details</h3>
                <div className="top-script-brief-card">
                                      <div className="top-script-brief-info">
                      <h4>Brief: {scriptStats.brief}</h4>
                      <p>Created on {scriptStats.createdDate}</p>
                    </div>
                  <button className="top-script-brief-btn" onClick={() => onViewBrief(script)}>
                    View Brief
                  </button>
                </div>
              </div>

              <div className="top-script-metrics-section">
                <h3>Script Performance Metrics</h3>
                <div className="top-script-metrics-grid">
                  <div className="top-script-metric-card">
                    <div className="top-script-metric-icon">
                      <Eye size={18} />
                    </div>
                    <div className="top-script-metric-content">
                      <span className="top-script-metric-value">{(scriptStats.totalViews / 1000).toFixed(0)}K</span>
                      <span className="top-script-metric-label">Total Views</span>
                    </div>
                  </div>
                  <div className="top-script-metric-card">
                    <div className="top-script-metric-icon">
                      <Target size={18} />
                    </div>
                    <div className="top-script-metric-content">
                      <span className="top-script-metric-value">{scriptStats.avgCTR}%</span>
                      <span className="top-script-metric-label">Avg CTR</span>
                    </div>
                  </div>
                  <div className="top-script-metric-card">
                    <div className="top-script-metric-icon">
                      <TrendingUp size={18} />
                    </div>
                    <div className="top-script-metric-content">
                      <span className="top-script-metric-value">{scriptStats.avgConversion}%</span>
                      <span className="top-script-metric-label">Conversion</span>
                    </div>
                  </div>
                  <div className="top-script-metric-card">
                    <div className="top-script-metric-icon">
                      <DollarSign size={18} />
                    </div>
                    <div className="top-script-metric-content">
                      <span className="top-script-metric-value">${(scriptStats.totalRevenue / 1000).toFixed(1)}K</span>
                      <span className="top-script-metric-label">Revenue</span>
                    </div>
                  </div>
                  <div className="top-script-metric-card">
                    <div className="top-script-metric-icon">
                      <Clock size={18} />
                    </div>
                    <div className="top-script-metric-content">
                      <span className="top-script-metric-value">{scriptStats.avgWatchTime}s</span>
                      <span className="top-script-metric-label">Watch Time</span>
                    </div>
                  </div>
                  <div className="top-script-metric-card">
                    <div className="top-script-metric-icon">
                      <Award size={18} />
                    </div>
                    <div className="top-script-metric-content">
                      <span className="top-script-metric-value">{scriptStats.completionRate}%</span>
                      <span className="top-script-metric-label">Completion</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'creators' && (
            <div className="top-script-creators-tab">
              <div className="top-script-creators-section">
                <h3>Top Performing Creators</h3>
                <div className="top-script-creators-list">
                  {scriptStats.topCreators.map((creator, index) => (
                    <div key={index} className="top-script-creator-item">
                      <div className="top-script-creator-avatar">
                        <img 
                          src={creator.avatar} 
                          alt={creator.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="top-script-creator-avatar-fallback">
                          {creator.name.split(' ').map(n => n[0]).join('')}
                        </div>
                      </div>
                      <div className="top-script-creator-info">
                        <h4>{creator.name}</h4>
                        <div className="top-script-creator-stats">
                          <span>{(creator.views / 1000).toFixed(0)}K views</span>
                          <span>{creator.ctr}% CTR</span>
                          <span>{creator.conversion}% conversion</span>
                        </div>
                      </div>
                      <div className="top-script-creator-rating">
                        <Star size={14} />
                        <span>{creator.rating}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="top-script-usage-section">
                <h3>Recent Usage</h3>
                <div className="top-script-usage-list">
                  {scriptStats.recentUsage.map((usage, index) => (
                    <div key={index} className={`top-script-usage-item ${usage.performance}`}>
                      <div className="top-script-usage-info">
                        <h4>{usage.creator}</h4>
                        <p>Used on {usage.date}</p>
                      </div>
                      <div className="top-script-usage-stats">
                        <span>{(usage.views / 1000).toFixed(0)}K views</span>
                        <span className={`performance-badge ${usage.performance}`}>
                          {usage.performance === 'excellent' ? 'Excellent' : 'Good'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'insights' && (
            <div className="top-script-insights-tab">
              <div className="top-script-insights-section">
                <h3>Script Performance Insights</h3>
                <div className="top-script-insights-grid">
                  {scriptInsights.map((insight, index) => (
                    <div key={index} className={`top-script-insight-card ${insight.type}`}>
                      <div className="top-script-insight-header">
                        <div className="top-script-insight-icon">
                          {insight.type === 'positive' && <CheckCircle size={16} />}
                          {insight.type === 'improvement' && <Target size={16} />}
                          {insight.type === 'warning' && <XCircle size={16} />}
                        </div>
                        <h4>{insight.title}</h4>
                      </div>
                      <p className="top-script-insight-text">{insight.insight}</p>
                      <div className="top-script-insight-recommendation">
                        <strong>Recommendation:</strong> {insight.recommendation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="top-script-quality-section">
                <h3>Script Quality Ratings</h3>
                <div className="top-script-quality-grid">
                  <div className="top-script-quality-card">
                    <div className="top-script-quality-header">
                      <h4>Hook Rating</h4>
                      <div className="top-script-quality-score">{scriptStats.hookRating}/5</div>
                    </div>
                    <p>How well the opening captures attention</p>
                  </div>
                  <div className="top-script-quality-card">
                    <div className="top-script-quality-header">
                      <h4>CTA Rating</h4>
                      <div className="top-script-quality-score">{scriptStats.ctaRating}/5</div>
                    </div>
                    <p>Effectiveness of call-to-action</p>
                  </div>
                  <div className="top-script-quality-card">
                    <div className="top-script-quality-header">
                      <h4>Overall Rating</h4>
                      <div className="top-script-quality-score">{scriptStats.avgRating}/5</div>
                    </div>
                    <p>Average creator rating</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopScriptModal;
